import { AudioOutlined, StopOutlined } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SpeechRecognition, {
  useSpeechRecognition,
} from 'react-speech-recognition';
import styles from './Dictaphone.module.scss';

export type VoiceProps =
  | {
      enableVoice: true;
      activeVoiceId: string | null;
      setActiveVoiceId: (id: string | null) => void;
    }
  | {
      enableVoice?: false;
      activeVoiceId?: never;
      setActiveVoiceId?: never;
    };

export type DictaphoneProps = {
  onTranscript?: (text: string) => void;
  className?: string;
  id: string;
  activeVoiceId: string | null;
  setActiveVoiceId: (id: string | null) => void;
};

export const useDictaphone = () => {
  const [activeVoiceId, setActiveVoiceId] = useState<string | null>(null);
  return useMemo(() => ({ activeVoiceId, setActiveVoiceId }), [activeVoiceId]);
};

const Dictaphone: FC<DictaphoneProps> = (props) => {
  const { onTranscript, activeVoiceId, id, setActiveVoiceId } = props;
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();
  const { t } = useTranslation();

  useEffect(() => {
    if (onTranscript && activeVoiceId === id && !listening && transcript) {
      onTranscript(transcript);
      resetTranscript();
    }
  }, [transcript, listening, activeVoiceId, onTranscript, id, resetTranscript]);

  if (!browserSupportsSpeechRecognition) {
    return <></>;
  }

  const handleClick = () => {
    if (!browserSupportsSpeechRecognition) return;

    if (listening && activeVoiceId === id) {
      SpeechRecognition.stopListening();
      setActiveVoiceId(null);
    } else {
      resetTranscript();
      setActiveVoiceId(id);
      SpeechRecognition.startListening({});
    }
  };

  return (
    <div className={styles.dictaphone}>
      <Tooltip
        title={
          activeVoiceId === id && listening
            ? t('dictaphone.stop')
            : t('dictaphone.start')
        }
      >
        <Button
          shape="circle"
          type={activeVoiceId === id && listening ? 'primary' : 'default'}
          size="small"
          danger={activeVoiceId === id && listening}
          icon={
            activeVoiceId === id && listening ? (
              <StopOutlined />
            ) : (
              <AudioOutlined />
            )
          }
          onClick={handleClick}
        />
      </Tooltip>
    </div>
  );
};
export default Dictaphone;
